/**
 * The cheap gate between a signal and a turn (RFC 0301 §3b).
 *
 * Rules, not judgement: it throws away what is plainly not worth a turn and
 * lets the turn decide the rest. Pure, over the pairing and the firings already
 * on the session's log, so every rule is a line a test can name.
 *
 * WHAT IT DOES NOT DROP. A comment is somebody saying something.
 * Cooldown and the hourly cap exist to stop a stream of edits spending turns,
 * and applying them to a person pointing at a paragraph would answer the
 * gesture with silence. Both still stand down while a turn for the pairing is
 * queued, because that turn has not read anything yet and will read this too.
 */

import type { Firing } from '../../log'
import type { FiringSignal } from '../../log/types'
import type { Pairing } from '../project/types'
export type GateDrop = 'disabled' | 'queued' | 'cooldown' | 'cap'

export type GateResult = { pass: true } | { pass: false; drop: GateDrop }

const ASKED: ReadonlySet<FiringSignal> = new Set(['comment'])

const HOUR_MS = 60 * 60 * 1000

export function gateSignal(
  signal: FiringSignal,
  pairing: Pairing,
  firings: Firing[],
  now: number = Date.now(),
): GateResult {
  if (!pairing.enabled) return { pass: false, drop: 'disabled' }
  const mine = firings.filter((f) => f.pairingId === pairing.id)
  // A queued turn for this pairing will read the log when it starts, so a
  // second signal now is already covered by it.
  if (mine.some((f) => f.runStatus === 'queued')) {
    return { pass: false, drop: 'queued' }
  }
  if (ASKED.has(signal)) return { pass: true }
  const last = mine.at(-1)
  if (last && now - last.at < pairing.cooldownMs) {
    return { pass: false, drop: 'cooldown' }
  }
  const lastHour = mine.filter((f) => now - f.at < HOUR_MS).length
  if (lastHour >= pairing.maxPerHour) return { pass: false, drop: 'cap' }
  return { pass: true }
}
